import { useState, createContext, useEffect } from "react";
import APIService from "../services/APIService";
import { DEFAULT_CURRENCY, currencyTags } from "../utils/global_const";
import { useParams } from "react-router-dom";

export const ProductContext = createContext();

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [searchedProducts, setSearchedProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState(null);
  const [brandProducts, setBrandProducts] = useState([]);
  const [standardPrices, setStandardPrices] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 24;
  const [currentPage, setCurrentPage] = useState(1);
  const [currencyTag, setCurrencyTag] = useState(
    currencyTags[localStorage.getItem("currency") || DEFAULT_CURRENCY]
  );
  //FILTER
  const [selectedBrands, setSelectedBrands] = useState(new Set());
  const [selectedNicotineRange, setSelectedNicotineRange] = useState(new Set());

  const [hideOutOfStock, setHideOutOfStock] = useState(false);
  const [hideOnRequest, setHideOnRequest] = useState(false);
  const [sortBy, setSortBy] = useState("");

  const [createdOrder, setCreatedOrder] = useState(null);

  const loadProducts = async (page = 1, filters = {}) => {
    const pathParts = window.location.pathname.split("/"); // ["", "de", "snus-verkauf", "apres"]
    const slugIndex = pathParts.findIndex((p) => p === "snus-verkauf");
    const slug =
      slugIndex !== -1 && pathParts.length > slugIndex + 1
        ? pathParts[slugIndex + 1]
        : null;

    try {
      const response = slug
        ? await APIService.GetProductsByBrand(slug, page, filters)
        : await APIService.GetPublicProducts(page, filters);

      setProducts(response.results || []);
      setTotalPages(response.count ? Math.ceil(response.count / pageSize) : 1);
    } catch (error) {
      console.error("Greška prilikom učitavanja proizvoda:", error);
    }
  };

  useEffect(() => {
    const filters = {};
    const nicotineRanges = [
      { label: "0-5 mg", range: [0, 5] },
      { label: "5-9 mg", range: [5, 9] },
      { label: "9-12 mg", range: [9, 12] },
      { label: "12-15 mg", range: [12, 15] },
      { label: "15-18 mg", range: [15, 18] },
      { label: "18-30 mg", range: [18, 30] },
      { label: "30-50 mg", range: [30, 50] },
    ];

    // Brendovi (ako ima selektovanih)
    const brands = Array.from(selectedBrands);
    if (brands.length > 0) {
      filters.brand = brands;
    }

    // ✅ Novo: više nikotinskih opsega
    const nicotineSelections = Array.from(selectedNicotineRange);
    if (nicotineSelections.length > 0) {
      const selectedRanges = nicotineSelections
        .map((label) => nicotineRanges.find((r) => r.label === label)?.range)
        .filter(Boolean);

      const flat = selectedRanges.flat();
      const min = Math.min(...flat);
      const max = Math.max(...flat);

      filters.nicotine_min = min;
      filters.nicotine_max = max;
    }

    // Stanja – dodaj samo ako nešto nije uključeno (tj. ima filtera)
    const allStates = ["in_stock", "on_request", "out_of_stock"];
    const filteredStates = [];

    if (!hideOutOfStock) filteredStates.push("out_of_stock");
    if (!hideOnRequest) filteredStates.push("on_request");
    filteredStates.push("in_stock");

    // samo ako NE uključujemo sva 3 stanja, šaljemo state filter
    const isDefault = allStates.every((s) => filteredStates.includes(s));
    if (!isDefault) {
      filters.state = filteredStates;
    }

    if (sortBy && sortBy !== "") {
      filters.ordering = sortBy;
    }

    loadProducts(currentPage, filters);
  }, [
    currentPage,
    selectedBrands,
    selectedNicotineRange,
    hideOutOfStock,
    hideOnRequest,
    sortBy,
  ]);

  const loadBrands = async () => {
    try {
      const response = await APIService.GetPublicBrands();
      setBrands(response);
    } catch (error) {
      console.error(error);
    }
  };

  const loadProductBySlug = async (slug) => {
    try {
      const response = await APIService.GetProductBySlug(slug);
      return response; // ili možeš postaviti u state ako želiš globalno čuvanje
    } catch (error) {
      console.error("Greška prilikom učitavanja proizvoda:", error);
      return null;
    }
  };
  const loadRecommendedProductsBySlug = async (slug) => {
    try {
      const response = await APIService.GetRecommendedProductsBySlug(slug);
      setRecommendedProducts(response);
    } catch (error) {
      console.error(
        "Greška prilikom učitavanja preporučenih proizvoda:",
        error
      );
      return [];
    }
  };

  const loadFeaturedProducts = async (page = 1) => {
    try {
      const data = await APIService.GetFeaturedProducts(page);
      setFeaturedProducts(data.results);
    } catch (error) {
      console.error("Error loading featured products:", error);
    }
  };
  const loadNewArrivalsProducts = async (page = 1) => {
    try {
      const data = await APIService.GetNewArrivalsProducts(page);
      setNewArrivals(data.results);
    } catch (error) {
      console.error("Error loading featured products:", error);
    }
  };

  const loadStandardBrandPrices = async () => {
    try {
      const response = await APIService.GetStandardBrandPrices();
      setStandardPrices(response);
    } catch (error) {
      console.error(error);
    }
  };

  const createOrder = async (orderData) => {
    try {
      const createdOrder = await APIService.createOrder(orderData);

      return createdOrder;
    } catch (error) {
      console.error("Error creating new address:", error);
      throw error;
    }
  };

  function updateCurrencyTag() {
    const storedCurrency = localStorage.getItem("currency");
    const tag = currencyTags[storedCurrency] || currencyTags[DEFAULT_CURRENCY];
    setCurrencyTag(tag);
  }

  useEffect(() => {
    updateCurrencyTag();
    loadProducts();
    loadBrands();
    loadStandardBrandPrices();
  }, []);

  const contextData = {
    products,
    brands,
    brand,
    brandProducts,
    standardPrices,
    totalPages,
    currentPage,
    selectedBrands,
    selectedNicotineRange,
    loadProducts,
    loadBrands,
    loadStandardBrandPrices,
    loadFeaturedProducts,
    loadNewArrivalsProducts,
    setSortBy,
    setBrand,
    setCurrentPage,
    //FILTER
    setSelectedBrands,
    setSelectedNicotineRange,
    hideOutOfStock,
    setHideOutOfStock,
    hideOnRequest,
    setHideOnRequest,
    sortBy,
    currencyTag,

    //single product
    product,
    setProduct,
    loadProductBySlug,
    recommendedProducts,
    featuredProducts,
    newArrivals,
    setSearchedProducts,
    searchedProducts,
    loadRecommendedProductsBySlug,
    createOrder,
    createdOrder,
    setCreatedOrder,
  };

  return (
    <ProductContext.Provider value={contextData}>
      {children}
    </ProductContext.Provider>
  );
};
